import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Building, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { laNeighborhoods } from "@/data/laNeighborhoods";
import { additionalNeighborhoods } from "@/data/moreNeighborhoods";
import SEO from "@/components/SEO";
import { generateFaqSchema, getCanonicalUrl } from "@/utils/seoUtils";
import PageTransition from "@/components/PageTransition";
import Navbar from "@/components/Navbar";

const allNeighborhoods = [...laNeighborhoods, ...additionalNeighborhoods];

// Group neighborhoods by region
const groupedByRegion = allNeighborhoods.reduce((acc, neighborhood) => {
  const region = neighborhood.region;
  if (!acc[region]) {
    acc[region] = [];
  }
  acc[region].push(neighborhood);
  return acc;
}, {} as Record<string, typeof allNeighborhoods>);

// Order regions
const regionOrder = ["Central", "West", "East", "South", "Valley", "North"];
const orderedRegions = regionOrder.filter(region => groupedByRegion[region]);

const NeighborhoodsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  
  // Filter neighborhoods based on search and active region
  const filteredNeighborhoods = allNeighborhoods.filter(neighborhood => {
    const matchesSearch = !searchTerm || 
      neighborhood.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      neighborhood.keyIndustries.some(industry => 
        industry.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesRegion = !activeRegion || neighborhood.region === activeRegion;
    
    return matchesSearch && matchesRegion;
  });
  
  // SEO data - Enhanced for better search rankings
  const title = "Top Los Angeles Neighborhood SEO Services | Local SEO Experts";
  const description = "Specialized SEO services for every Los Angeles neighborhood. Find expert digital marketing strategies tailored to your local area for businesses in Hollywood, Santa Monica, Beverly Hills, and all LA County neighborhoods.";
  
  const faqSchema = generateFaqSchema([
    {
      question: "Why is local neighborhood SEO important for Los Angeles businesses?",
      answer: "Local neighborhood SEO helps your business appear in location-specific searches made by potential customers in your area. In a city as large and diverse as Los Angeles, neighborhood-specific SEO ensures you reach the right local audience, understand local competition, and optimize for the unique search behaviors of residents in different LA neighborhoods."
    },
    {
      question: "How does SEO differ across Los Angeles neighborhoods?",
      answer: "Each Los Angeles neighborhood has its own unique demographic, business landscape, and local search patterns. For example, SEO for a business in tech-focused Culver City will differ from a business in tourism-heavy Santa Monica. We tailor strategies to reflect local industry strengths, consumer behavior, and competitive landscapes specific to each neighborhood."
    },
    {
      question: "How long does it take to see results from neighborhood-specific SEO?",
      answer: "While results vary based on competition in your specific Los Angeles neighborhood and industry, most businesses begin to see improvement in local search visibility within 3-6 months. Local SEO often shows results faster than broad SEO campaigns because neighborhood-specific keywords typically have less competition than citywide or national terms."
    }
  ]);

  return (
    <>
      <Navbar />
      <PageTransition>
        <SEO 
          seo={{
            title,
            description,
            ogImage: "/og-image.png",
            jsonLd: faqSchema,
            canonical: getCanonicalUrl("/neighborhoods"),
            keywords: "Los Angeles neighborhood SEO, local SEO Los Angeles, neighborhood-specific SEO, LA local search optimization, Los Angeles area SEO services, Hollywood SEO, Santa Monica SEO, Beverly Hills SEO, Culver City SEO"
          }}
          url={getCanonicalUrl("/neighborhoods")}
        />
        
        <section className="bg-soft-gradient pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
              >
                Local SEO Specialists
              </motion.span>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl font-bold mb-6"
              >
                #1 SEO Services Across 
                <span className="text-gradient bg-primary-gradient ml-2">
                  Los Angeles
                </span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg text-foreground/80"
              >
                Specialized SEO strategies for every Los Angeles neighborhood. Our local experts understand the unique aspects of each community and provide tailored solutions to help your business grow.
              </motion.p>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="relative max-w-xl mx-auto mb-16"
            >
              <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search neighborhoods or industries..."
                className="pl-10 h-12 bg-white/60 dark:bg-background/60 backdrop-blur-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-3 mb-16"
            >
              <Button 
                variant={!activeRegion ? "soft" : "outline"} 
                onClick={() => setActiveRegion(null)}
                className="mb-2"
              >
                All Regions
              </Button>
              
              {orderedRegions.map((region) => (
                <Button
                  key={region}
                  variant={activeRegion === region ? "soft" : "outline"}
                  onClick={() => setActiveRegion(activeRegion === region ? null : region)}
                  className="mb-2"
                >
                  {region} LA
                </Button>
              ))}
            </motion.div>
            
            {filteredNeighborhoods.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNeighborhoods.map((neighborhood, index) => (
                  <motion.div
                    key={neighborhood.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
                  >
                    <Card className="h-full hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <Badge variant="soft" className="mb-2">
                            {neighborhood.region}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl">{neighborhood.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Building className="h-4 w-4" />
                          <span>{neighborhood.businessCount.toLocaleString()}+ Businesses</span>
                        </div>
                        <p className="text-sm line-clamp-3">
                          Known for {neighborhood.knownFor}. Top industries include {neighborhood.keyIndustries.slice(0, 3).join(", ")}.
                        </p>
                      </CardContent>
                      <CardFooter>
                        <Button asChild variant="soft" className="w-full">
                          <Link to={`/neighborhood/${neighborhood.id}`} className="flex items-center justify-center gap-1">
                            <span>SEO in {neighborhood.name}</span>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No neighborhoods found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <Button variant="soft" onClick={() => {
                  setSearchTerm("");
                  setActiveRegion(null);
                }}>
                  Reset Filters
                </Button>
              </div>
            )}
            
            <div className="mt-20 max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Why is local neighborhood SEO important for Los Angeles businesses?</h3>
                  <p className="text-foreground/80">
                    Local neighborhood SEO helps your business appear in location-specific searches made by potential customers in your area. In a city as large and diverse as Los Angeles, neighborhood-specific SEO ensures you reach the right local audience, understand local competition, and optimize for the unique search behaviors of residents in different LA neighborhoods.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">How does SEO differ across Los Angeles neighborhoods?</h3>
                  <p className="text-foreground/80">
                    Each Los Angeles neighborhood has its own unique demographic, business landscape, and local search patterns. For example, SEO for a business in tech-focused Culver City will differ from a business in tourism-heavy Santa Monica. We tailor strategies to reflect local industry strengths, consumer behavior, and competitive landscapes specific to each neighborhood.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">How long does it take to see results from neighborhood-specific SEO?</h3>
                  <p className="text-foreground/80">
                    While results vary based on competition in your specific Los Angeles neighborhood and industry, most businesses begin to see improvement in local search visibility within 3-6 months. Local SEO often shows results faster than broad SEO campaigns because neighborhood-specific keywords typically have less competition than citywide or national terms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
};

export default NeighborhoodsPage;
